import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CredentialAssigneeOption } from '@/types';
import {
  type CredentialFormData,
  CredentialFormFields,
  emptyCredentialForm,
} from './CredentialFormFields';

interface CreateCredentialDialogProps {
  eventSlug: string;
  assignableOptions: CredentialAssigneeOption[];
  credentialTypes: string[];
}

export function CreateCredentialDialog({
  eventSlug,
  assignableOptions,
  credentialTypes,
}: CreateCredentialDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm<CredentialFormData>(emptyCredentialForm());
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/credentials`, {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add printed credential
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add printed credential</DialogTitle>
            <DialogDescription>
              Register a card that exists physically. Leave it unassigned for
              blank cards handed out at the door.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <CredentialFormFields
              data={data}
              setData={setData}
              errors={errors}
              isPending={processing}
              assignableOptions={assignableOptions}
              credentialTypes={credentialTypes}
            />

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  reset();
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
